import { ThemedCard } from '@/app/platform/core/layout/themes/components/ThemedCard.tsx';
import { ThemedDivider } from '@/app/platform/core/layout/themes/components/ThemedDivider.tsx';
import { ThemedLoadingSpinner } from '@/app/platform/core/layout/themes/components/ThemedLoadingSpinner.tsx';
import { ThemedText } from '@/app/platform/core/layout/themes/components/ThemedText.tsx';
import { ProjectCard } from '@/app/features/tribes-projects/projects/ProjectCard.tsx';
import { AppLayout } from '@/app/platform/core/layout/AppLayout.tsx';
import { ThemeProvider } from '@/app/platform/core/layout/themes/ThemeContext.tsx';
import { useCurrentUserProfile } from '@/app/platform/functions/people/users/useCurrentUserProfile.ts';
import { useUserProjects } from '@/app/features/tribes-projects/projects/useProjects.ts';
import { MenuAction } from '@/app/platform/core/layout/menu.types.ts';
import { ProjectEntry } from '@/app/features/tribes-projects/projects/projects.query.types.ts';
import { BookmarkToggle } from '@/app/features/bookmarks/BookmarkToggle.tsx';
import { buildBookmarkDescription } from '@/app/features/bookmarks/types.ts';

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const ProjectsPageContent: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, isLoading: currentUserLoading } = useCurrentUserProfile();
  const { projects, loading: projectsLoading } = useUserProjects(
    user?.id || '',
    { enabled: !!user?.id },
  );

  const breadcrumbs = useMemo(
    () => [
      { label: t('common.home'), path: '/app' },
      { label: t('projects.title') },
    ],
    [t],
  );

  const dedupedProjects = useMemo((): ProjectEntry[] => {
    const map = new Map<string, ProjectEntry>();
    for (const row of projects) {
      const existing = map.get(row.project_id);
      if (!existing) {
        map.set(row.project_id, {
          project_id: row.project_id,
          project_url_param_id: row.project_url_param_id,
          project_name: row.project_name,
          direct_position: row.via_represents ? null : row.effective_position,
          represented_persons:
            row.via_represents && row.person_first_name && row.person_last_name
              ? [
                  {
                    first_name: row.person_first_name,
                    last_name: row.person_last_name,
                    position: row.effective_position,
                  },
                ]
              : [],
        });
      } else {
        if (!row.via_represents) {
          existing.direct_position = row.effective_position;
        } else if (row.person_first_name && row.person_last_name) {
          existing.represented_persons.push({
            first_name: row.person_first_name,
            last_name: row.person_last_name,
            position: row.effective_position,
          });
        }
      }
    }
    return Array.from(map.values());
  }, [projects]);

  const menuActions = useMemo(
    (): MenuAction[] => [
      {
        id: 'projects.backToTribes',
        icon: 'arrow-left',
        label: t('tribes.title'),
        path: '/app/tribes',
      },
    ],
    [t],
  );

  if (currentUserLoading || projectsLoading) {
    return (
      <AppLayout breadcrumbs={breadcrumbs} menuActions={menuActions}>
        <ThemedLoadingSpinner />
      </AppLayout>
    );
  }

  return (
    <AppLayout
      breadcrumbs={breadcrumbs}
      menuActions={menuActions}
      bookmarkSlot={<BookmarkToggle pagePath={location.pathname} pageTitle={t('projects.title')} pageDescription={buildBookmarkDescription(breadcrumbs)} />}
    >
      <ThemedText variant="primary" size="small">
        {t('projects.subtitle')}
      </ThemedText>

      <ThemedDivider variant="primary" />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {dedupedProjects.map((project) => (
          <ProjectCard key={project.project_id} project={project} />
        ))}
      </div>

      {dedupedProjects.length === 0 && (
        <ThemedCard variant="secondary">
          <ThemedText variant="secondary" size="medium">
            {t('projects.empty')}
          </ThemedText>
        </ThemedCard>
      )}
    </AppLayout>
  );
};

export const ProjectsPage: React.FC = () => (
  <ThemeProvider defaultTheme="default">
    <ProjectsPageContent />
  </ThemeProvider>
);
